import {
	getGetProjectQueryOptions,
	getListMeasurementsQueryOptions,
	getListNetworkConnectionsSuspenseQueryOptions,
	getListNetworkNodesSuspenseQueryOptions,
	getListProjectNetworksQueryOptions,
	type Network,
	type Project,
	useGetProjectSuspense,
	useListProjectNetworksSuspense,
} from '@amarantos/api-client'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { ChevronLeft, Eye, Pencil } from 'lucide-react'
import { type ReactNode, Suspense, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiErrorBoundary } from '@/components/api-error-boundary'
import { EditorButtonGroup } from '@/components/editor-button-group'
import { EditorHeaderSlotProvider } from '@/components/editor-header-slot'
import { EditorTitle } from '@/components/editor-title'
import type { CanvasViewport } from '@/components/network-graph/network-graph'
import { NetworkGraphSkeleton } from '@/components/network-graph/network-graph-skeleton'
import { NetworkTypeBadge } from '@/components/network-type-badge'
import { RouteErrorFallback } from '@/components/route-error-fallback'
import { Button } from '@/components/ui/button'
import { PortalSurfaceProvider } from '@/components/ui/portal-surface'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { NetworkSwitch } from '@/features/planning/components/network-switch'
import { PlanningWorkspaceFullscreen } from '@/features/planning/components/planning-workspace-fullscreen'
import { useEditorSelection } from '@/features/planning/hooks/use-editor-selection'
import { switchView } from '@/features/planning/utils/editor-selection'
import {
	type EditorView,
	editorSearchSchema,
	resolveDefaultNetworkId,
	toListProjectNetworksParams,
} from '@/features/planning/utils/search'
import { PreviewWorkspaceFullscreen, toListNetworkMeasurementsParams } from '@/features/preview'
import { toListNetworkElementsParams } from '@/hooks/use-network-elements'
import { useWholeListTruncationWarning } from '@/hooks/use-whole-list-truncation-warning'

import { getApiErrorMessage } from '@/utils/errors'
import { cn } from '@/utils/utils'

// The trailing underscore on `$projectId_` opts this route OUT of the `$projectId` layout (its tab
// bar, breadcrumb and padded content shell). It keeps the URL `/projects/:id/planning-workspace`.
//
// Both lenses live behind the project's single Sieci list — a row's Edycja / Podgląd button opens the
// editor on that lens, the in-editor Edycja⇆Podgląd switch flips between them, and the back button
// returns to the list.
export const Route = createFileRoute('/_authenticated/projects/$projectId_/planning-workspace')({
	params: {
		parse: ({ projectId }) => ({ projectId: Number(projectId) }),
		stringify: ({ projectId }) => ({ projectId: String(projectId) }),
	},
	validateSearch: editorSearchSchema,
	// The editor opens on a network, so a URL naming none opens on the project's first — a gate, like the
	// role gates in `utils/roles.ts`, rather than a component effect: the canvas paints that plan directly
	// instead of flashing the select prompt on the way, and the URL carries the network being shown, so a
	// reload or a shared link reopens the same plan. A network already in the URL is the user's choice and
	// is left alone — that path never waits on the network list, which the loader below prefetches.
	beforeLoad: async ({ context: { queryClient }, params: { projectId }, search: { networkId } }) => {
		if (networkId) {
			return
		}

		const networks = await queryClient.ensureQueryData(
			getListProjectNetworksQueryOptions(projectId, toListProjectNetworksParams()),
		)
		const defaultNetworkId = resolveDefaultNetworkId(networks.data)

		if (defaultNetworkId) {
			throw redirect({
				to: '/projects/$projectId/planning-workspace',
				params: { projectId },
				search: (previous) => ({ ...previous, networkId: defaultNetworkId }),
				// The network-less URL was never a place to go back to — it only ever stood in for this one.
				replace: true,
			})
		}
	},
	loaderDeps: ({ search: { networkId, view } }) => ({ networkId, view }),
	loader: ({ context: { queryClient }, params: { projectId }, deps: { networkId, view } }) => {
		queryClient.ensureQueryData(getGetProjectQueryOptions(projectId))
		queryClient.ensureQueryData(getListProjectNetworksQueryOptions(projectId, toListProjectNetworksParams()))
		if (networkId) {
			const params = toListNetworkElementsParams()
			queryClient.ensureQueryData(getListNetworkNodesSuspenseQueryOptions(networkId, params))
			queryClient.ensureQueryData(getListNetworkConnectionsSuspenseQueryOptions(networkId, params))
			if (view === 'preview') {
				queryClient.ensureQueryData(getListMeasurementsQueryOptions(toListNetworkMeasurementsParams(networkId)))
			}
		}
	},
	errorComponent: ({ error, reset }) => (
		<RouteErrorFallback error={error} reset={reset} fallbackMessageKey='error.load_networks' />
	),
	component: ProjectPlanningWorkspaceRoute,
})

function ProjectPlanningWorkspaceRoute() {
	const { t } = useTranslation()
	const { projectId } = Route.useParams()
	const search = Route.useSearch()
	const navigate = Route.useNavigate()

	const project = useGetProjectSuspense(projectId).data
	const networksQuery = useListProjectNetworksSuspense(projectId, toListProjectNetworksParams())
	useWholeListTruncationWarning('networks', networksQuery.data)
	const networks = networksQuery.data.data

	const selectedNetwork = networks.find((network) => network.id === search.networkId) ?? null

	const selection = useEditorSelection({ search, navigate })

	// Both views dock a full-height panel beside the canvas for as long as a network is open — selected or
	// not, the dock holds its width and fills with an empty state — so the chrome gives up the right edge
	// the whole time, rather than the view switch sliding out from under it on every click.
	const isPanelOpen = !!selectedNetwork

	// Zoom + pan kept across the Edycja⇆Podgląd switch so the plan holds still when the lens changes. This
	// route outlives that switch — only the child view *type* swaps under it — so the viewport lives here, in
	// a ref a continuous pan/zoom can write without re-rendering the editor. It carries the network it was
	// taken in, so switching networks refits the new plan instead of dropping it into the old one's frame;
	// nothing persists it past this mount, so a reload reopens fitted.
	const viewportRef = useRef<{ networkId: Network['id']; viewport: CanvasViewport } | null>(null)

	const rememberViewport = (viewport: CanvasViewport) => {
		if (selectedNetwork) {
			viewportRef.current = { networkId: selectedNetwork.id, viewport }
		}
	}

	const initialViewport =
		selectedNetwork && viewportRef.current?.networkId === selectedNetwork.id ? viewportRef.current.viewport : undefined

	// The title row's middle is the open view's to fill (its buttons count elements only it fetches), but the
	// row itself is this route's — so the box is rendered here and handed down. See `EditorHeaderSlot`.
	const [headerSlot, setHeaderSlot] = useState<HTMLDivElement | null>(null)

	// Held here for the same reason as the viewport: a collapsed element list stays collapsed across the view switch.
	const [elementListOpen, setElementListOpen] = useState(true)

	// Both lenses were opened from the project's Sieci list, so both go back there.
	const backLink: ReactNode = (
		<Link to='/projects/$projectId/networks' params={{ projectId }}>
			<ChevronLeft />
		</Link>
	)

	return (
		// A real fullscreen editor: `fixed inset-0` covers the whole app shell (sidebar included), on the
		// app's one light surface like everything else.
		//
		// Popovers, select menus and dialogs portal to <body>, where the editor's classes don't reach them.
		// `PortalSurfaceProvider` hands them `editor-dense` so comboboxes/selects opened from the dock keep
		// its compact sizing — there's no colour class to carry any more, since the editor isn't a second
		// theme.
		<PortalSurfaceProvider value='editor-dense'>
			<EditorHeaderSlotProvider value={headerSlot}>
				<div className='fixed inset-0 z-50 bg-background text-foreground'>
					<div className='absolute inset-0'>
						{selectedNetwork ? (
							<ApiErrorBoundary
								resetKey={`${search.view}-${selectedNetwork.id}`}
								fallback={(error) => (
									<div className='flex h-full items-center justify-center p-6 text-center text-destructive text-sm'>
										{getApiErrorMessage(error, t('error.load_network_plan'))}
									</div>
								)}
							>
								<Suspense fallback={<NetworkGraphSkeleton />}>
									{/* Remount on network switch so the canvas reloads + refits. */}
									{/* The open lens follows `view` — the read-only status canvas, or the authoring one. */}
									{search.view === 'preview' ? (
										<PreviewWorkspaceFullscreen
											key={selectedNetwork.id}
											projectId={projectId}
											networkId={selectedNetwork.id}
											rotation={selectedNetwork.rotation}
											initialViewport={initialViewport}
											onViewportChange={rememberViewport}
											selection={selection}
											elementListOpen={elementListOpen}
											onElementListOpenChange={setElementListOpen}
										/>
									) : (
										<PlanningWorkspaceFullscreen
											key={selectedNetwork.id}
											projectId={projectId}
											networkId={selectedNetwork.id}
											rotation={selectedNetwork.rotation}
											initialViewport={initialViewport}
											onViewportChange={rememberViewport}
											selection={selection}
											elementListOpen={elementListOpen}
											onElementListOpenChange={setElementListOpen}
										/>
									)}
								</Suspense>
							</ApiErrorBoundary>
						) : (
							<div className='flex h-full items-center justify-center p-6 text-center text-muted-foreground text-sm'>
								{t('planning.content.select_prompt')}
							</div>
						)}
					</div>

					{/* Editor chrome — title bar top-left over the dark canvas, then the open view's own buttons and
				    the view switch, both pushed to the opposite edge (`ml-auto` on the slot they share), above the
				    canvas controls. The right padding clears the dock (w-80) plus the chrome's own gutter when a
				    panel is open, so the switch lands beside the dock rather than on top of it, and a long project
				    name truncates instead of sliding under it. */}
					<div
						className={cn(
							// Every box in this row is its own object, so they stand a gap apart that reads as one —
							// wider than the 0.25rem their own contents sit apart by inside them, which is a seam.
							'pointer-events-none absolute inset-x-0 top-0 z-10 flex items-start gap-3 p-4',
							isPanelOpen && 'pr-84',
						)}
					>
						<EditorTitle
							className='pointer-events-auto'
							backButton={
								<Button asChild variant='ghost' size='icon' aria-label={t('planning.workspace.back_aria')}>
									{backLink}
								</Button>
							}
							// Ahead of the name, as in the Sieci list: the type is what the name is a name *for*, and the
							// editor is the one place showing a single network — nothing else here says which kind it is.
							badge={!!selectedNetwork && <NetworkTypeBadge type={selectedNetwork.type} />}
							subtitle={project.name}
						>
							<NetworkSwitch projectId={projectId} networks={networks} activeNetwork={selectedNetwork} />
						</EditorTitle>

						{/* Filled by the open view (see `EditorHeaderSlot`) — empty in Podgląd, which is why the
					    `ml-auto` that pushes this end of the row right lives here rather than on the switch. Whatever
					    a view puts here is a box of this row like any other, so it keeps the row's own gap. */}
						<div ref={setHeaderSlot} className='pointer-events-auto ml-auto flex shrink-0 items-start gap-3' />

						{!!selectedNetwork && <ViewSwitch projectId={projectId} activeView={search.view} />}
					</div>
				</div>
			</EditorHeaderSlotProvider>
		</PortalSurfaceProvider>
	)
}

// The two editor views are the same per-network buttons as the Sieci list (Pencil / Eye), so the
// switch reuses their labels and icons.
const EDITOR_VIEW_BUTTONS = [
	{ view: 'planning', labelKey: 'projects.detail.networks.actions.planning', Icon: Pencil },
	{ view: 'preview', labelKey: 'projects.detail.networks.actions.preview', Icon: Eye },
] as const satisfies readonly { view: EditorView; labelKey: string; Icon: typeof Pencil }[]

interface ViewSwitchProps {
	projectId: Project['id']
	activeView: EditorView
}

/**
 * The mark on the side the editor is currently showing. `bg-accent` — what a pressed tool button wears —
 * is also exactly what `variant='ghost'` fills on hover, so the two icons here read as the same button in
 * two states only while the pointer is away from them: hover the inactive side and the switch says both.
 * A toggle whose whole job is to say which view you are in cannot afford that, so the active side inverts
 * to `primary` instead, several steps clear of any hover, restating the hover fill so it doesn't fall back
 * to the ghost variant's own `hover:bg-accent` under the pointer.
 */
const ACTIVE_VIEW_CLASS = 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground'

/**
 * Edycja ⇆ Podgląd toggle — its own box on the editor's right edge, top corner, over the dock it switches
 * the contents of. The view lives in the URL, so each side is a link that keeps the network and swaps
 * `view`, carrying the selected element across with it — the canvas below stays put and its lens changes,
 * and the dock stays docked on the same element so the switch is a change of lens, not of subject. Only
 * `manhole` is dropped: it is the in-progress author form, which lives in Edycja alone.
 *
 * Icon-only, like the tool box, so it borrows that box's tooltips to name the two sides: same 500ms delay,
 * pointing inward from this edge (`bottom`) as the toolbar's point inward from its own (`right`).
 */
function ViewSwitch({ projectId, activeView }: ViewSwitchProps) {
	const { t } = useTranslation()

	return (
		<EditorButtonGroup className='pointer-events-auto inline-flex shrink-0'>
			{EDITOR_VIEW_BUTTONS.map(({ view, labelKey, Icon }) => {
				const isActive = view === activeView
				const label = t(labelKey)

				return (
					<Tooltip key={view}>
						<TooltipTrigger asChild>
							<Button
								asChild
								variant='ghost'
								size='icon'
								className={cn(isActive ? ACTIVE_VIEW_CLASS : 'text-muted-foreground')}
							>
								<Link
									to='/projects/$projectId/planning-workspace'
									params={{ projectId }}
									search={(previous) => switchView(previous, view)}
									aria-current={isActive ? 'page' : undefined}
									aria-label={label}
								>
									<Icon />
								</Link>
							</Button>
						</TooltipTrigger>
						<TooltipContent side='bottom'>{label}</TooltipContent>
					</Tooltip>
				)
			})}
		</EditorButtonGroup>
	)
}
